import {routesConfig} from './routes.config'

// ── Menu search index ────────────────────────────────────────────────────────
// The sidebar tree only renders `displayInSidebar` routes and their non-hidden
// `menuItems` — 34 parents + ~90 children out of ~140 reachable pages. Filtering that
// tree in place (the old approach) meant search could never find:
//   • tabs of consolidated pages (`hideInSidebar: true`)
//   • settings sub-pages and config screens, which have no sidebar entry at all
//   • a page by any word other than the exact substring of its current label
// This module builds a flat index over *every* reachable destination instead, so the
// sidebar can render search results as a list rather than a pruned tree.

// Extra search terms per pathname. Sidebar labels are short and get renamed over time
// (e.g. "Weight Discrepancy" → "Weight Management"), which silently breaks the words
// merchants actually type. Keep synonyms and old names here.
const KEYWORDS = {
  '/home': 'landing start overview',
  '/dashboard': 'kpi metrics analytics common',
  '/d2c-dashboard': 'b2c kpi metrics analytics',
  '/analytics': 'ct control tower performance insights',
  '/proship-pilot': 'ai assistant copilot chat propilot',
  '/pace/about': 'pace ai courier allocation automated smart recommendation',

  // D2C shipments
  '/orders': 'b2c order awb shipment list forward',
  '/create-shipment': 'new order single bulk forward reverse create',
  '/create-order': 'new single order create forward',
  '/create-order-rvp': 'new reverse return rvp create',
  '/bulk-order-create': 'bulk upload excel csv order create',
  '/labels-invoices': 'download print label invoice manifest',
  '/download-labels-b2c': 'download print shipping label',
  '/download-invoice': 'download print invoice bill',
  '/download-invoice-label-merge-b2c': 'merge download label invoice combined',
  '/stuck-orders': 'stuck exception not moving',
  '/pending-pickups': 'manifest pending pickup handover',
  '/bulk-tracking': 'track awb upload bulk',
  '/update-payment-mode': 'cod prepaid convert payment mode',
  '/channel-orders': 'webstore channel shopify woocommerce magento amazon flipkart integration unfulfilled',

  // Returns
  '/returns': 'return reverse rvp pickup',
  '/reverse-orders': 'reverse rvp return',
  '/return-orders': 'return request',
  '/rvp/qc': 'quality check qc reverse',

  // NDR / NPR
  '/ndr': 'non delivery report undelivered ndr',
  '/ndr/call-recordings': 'call recording ivr audio',
  '/ndr/whatsapp-conversations': 'whatsapp chat conversation',
  '/ndr/automation': 'automation rule reattempt',
  '/npr': 'non pickup report npr',

  // Weight
  '/weight-discrepancy': 'weight discrepancy dispute volumetric chargeable mismatch',
  '/weight-discrepancy-bulk-action': 'weight discrepancy dispute bulk accept reject upload',

  '/claims': 'claim lost damaged compensation reimbursement',
  '/rate-calculator': 'rate price freight cost estimate calculator',
  '/order-creation-latency': 'latency sla speed order creation',

  // Downloads / rules
  '/zone-mapping': 'zone mapping pincode export',
  '/download-pod': 'pod proof of delivery signature download',
  '/merchant-rules': 'rule allocation courier priority shipping',

  // PTL
  '/ptl-dashboard': 'b2b ptl kpi metrics',
  '/ptl-delivery-health': 'b2b ptl delivery health today abd non abd scheduled edd visibility',
  '/appointments/overdue': 'b2b ptl abd overdue appointment missed delay',
  '/nonabd-edd-breaches': 'b2b ptl non abd edd breach late delay attribution client prozo',
  '/ptl-delivery-health/pod-analytics': 'b2b ptl pod proof of delivery pending aging received',
  '/ptl-dashboard/performance-overview': 'b2b ptl performance overview first attempt missed appointment attribution report',
  '/ptl-all-shipments': 'b2b ptl shipment all',
  '/create-b2b-orders': 'b2b ptl create new shipment',
  '/create-mps-orders': 'mps multi piece b2b create',
  '/upload-b2b-orders': 'b2b ptl bulk upload excel create',
  '/create-pickup-request': 'pickup request create b2b',
  '/ptl-pickup-requests': 'pickup request list b2b',
  '/b2b_tracking_upload': 'track upload b2b ptl',
  '/download-labels-b2b': 'download label b2b ptl bulk',
  '/bulk-mark-rto': 'rto mark bulk return',
  '/b2b-orders': 'non abd b2b ptl shipment',
  '/appointments/all': 'abd appointment based delivery b2b',
  '/ptl-courier-performance': 'courier partner performance b2b ptl sla',
  '/rate-calculator-ptl': 'rate price freight cost estimate calculator b2b ptl',
  '/zone-mapping-ptl': 'zone mapping pincode export b2b ptl',
  '/download-pod-ptl': 'pod proof of delivery signature download b2b ptl',
  '/merchant-rules-ptl': 'rule allocation courier priority shipping b2b ptl',

  // Account
  '/wallet': 'wallet money balance recharge payment finance billing',
  '/billing': 'cod remittance settlement payout',
  '/wallet-recharges': 'recharge topup add funds payment',
  '/passbook': 'passbook transaction statement ledger history',
  '/credit': 'credit limit postpaid',
  '/merchant-invoice': 'invoice billing gst tax',
  '/serviceable-pincodes': 'serviceability pincode pin code coverage serviceable',
  '/serviceable-pincodes/check-serviceability': 'check serviceability pincode pin code',
  '/serviceable-pincodes/download': 'download serviceability pincode export',
  '/dashboard/courier': 'courier partner performance sla d2c',
  '/reports': 'report export mis download',
  '/download-mis-from-awb': 'mis awb export download',
  '/sorter-image-download': 'sorter image download photo',
  '/support-v2': 'help support ticket escalation raise issue contact complaint freshdesk',
  '/escalation_management': 'help support ticket escalation raise issue contact complaint',
  '/setting': 'setting config configuration preferences',
  '/profile': 'profile kyc verification document gst pan bank account company',
  '/allupdates': 'alert update notice operations announcement',
  '/whats-new': 'release notes whats new changelog version update',
}

// Routable destinations with no sidebar entry of their own — settings sub-pages and
// config screens reachable only by clicking through another page. `parent` is the
// breadcrumb shown on the result row.
const UNLISTED = {
  '/merchant-step2': {label: 'Pickup Locations', parent: 'Settings', keywords: 'pickup location warehouse address add'},
  '/merchant-step3': {label: 'Users & Roles', parent: 'Settings', keywords: 'user team member role access invite'},
  '/delivery-location': {label: 'Delivery Locations', parent: 'Settings', keywords: 'delivery location address drop'},
  '/merchant-preview': {label: 'Merchant Details', parent: 'Settings', keywords: 'merchant profile preview company details'},
  '/setting/package': {label: 'Packages', parent: 'Settings', keywords: 'package box dimension size master'},
  '/rate-card': {label: 'Rate Card', parent: 'Settings', keywords: 'rate card pricing tariff price'},
  '/view-webhook': {label: 'Webhooks', parent: 'Settings', keywords: 'webhook api integration callback'},
  '/configue-webhook': {label: 'Configure Webhook', parent: 'Settings', keywords: 'webhook api integration setup configure'},
  '/courier-priority': {label: 'Courier Priority', parent: 'Settings', keywords: 'courier priority allocation preference'},
  '/custom-rule': {label: 'Custom Rules', parent: 'Settings', keywords: 'custom rule allocation condition'},
  '/cod-on-hold-config': {label: 'COD On Hold Config', parent: 'Settings', keywords: 'cod hold config remittance'},
  '/email-config': {label: 'Email Configuration', parent: 'Settings', keywords: 'email notification smtp config template'},
  '/communication-config': {label: 'Communication Configuration', parent: 'Settings', keywords: 'sms whatsapp email communication config template'},
  '/notification-config': {label: 'Notification Configuration', parent: 'Settings', keywords: 'notification alert config'},
  '/pickup-drop-mapping': {label: 'Setup Pickup Drop Mapping', parent: 'Settings', keywords: 'pickup drop mapping warehouse state pincode edd estimated delivery date shopify store'},
  '/notification': {label: 'Notifications', parent: 'Settings', keywords: 'notification alert inbox'},
  '/change-password': {label: 'Change Password', parent: 'Profile & KYC', keywords: 'password change reset security'},
  // Shared by both verticals (roles: ALL_ORDERS), so the breadcrumb stays vertical-neutral.
  '/tracking-list': {label: 'Tracking Results', parent: 'Tracking', keywords: 'track tracking awb result status'},
}

// Short group names for the result breadcrumb. Several labels are reused verbatim across
// the two verticals ("Downloads", "Shipping Rules", "Zone Mapping"), so the parent label
// alone can't tell two results apart.
const GROUP_LABELS = {
  AI_TOOLS: 'AI',
  GENERAL: 'General',
  D2C_FREIGHT: 'D2C',
  PTL_FREIGHT: 'PTL',
  ACCOUNT: 'Account',
  SUPPORT: 'Updates',
}

const norm = (s) => (s || '').toLowerCase()

const breadcrumb = (...parts) => parts.filter(Boolean).join(' · ')

/**
 * Flat, searchable list of every reachable destination. Built once — routesConfig is a
 * module-level constant, so the index never needs to be rebuilt.
 */
export function buildMenuSearchIndex() {
  const entries = []
  const seen = new Set()

  const push = (entry) => {
    // Parameterised routes (/claims/:claimId) aren't destinations you can navigate to
    // blind. Parents commonly share a pathname with their first child — first wins.
    if (!entry.pathname || entry.pathname.includes(':') || seen.has(entry.pathname)) return
    seen.add(entry.pathname)
    entries.push(entry)
  }

  routesConfig.forEach((route) => {
    const routeKeywords = KEYWORDS[route.pathname]

    if (route.menuItems?.length) {
      route.menuItems.forEach((item) => {
        push({
          pathname: item.pathname,
          label: item.label,
          parentLabel: breadcrumb(GROUP_LABELS[route.group], route.label),
          icon: route.icon,
          badge: item.badge,
          roles: route.roles,
          allowedEmailDomains: route.allowedEmailDomains,
          vertical: route.vertical,
          // PrivateRoute gates on the parent's flag first, then the child's.
          configs: [route.enableForConfig, item.enableForConfig].filter(Boolean),
          terms: norm([item.label, KEYWORDS[item.pathname], item.pathname].filter(Boolean).join(' ')),
          parentName: norm([route.label, GROUP_LABELS[route.group]].filter(Boolean).join(' ')),
          parentKeywords: norm(routeKeywords || ''),
        })
      })
      return
    }

    const unlisted = UNLISTED[route.pathname]
    const label = route.label || unlisted?.label
    if (!label) return // detail/preview screens with no name — nothing useful to match on

    push({
      pathname: route.pathname,
      label,
      parentLabel: breadcrumb(GROUP_LABELS[route.group], unlisted?.parent),
      icon: route.icon,
      roles: route.roles,
      allowedEmailDomains: route.allowedEmailDomains,
      vertical: route.vertical,
      // Same gate the tree applies: a hidden page must not be reachable through search.
      requiresDirectPlan: route.requiresDirectPlan,
      configs: [route.enableForConfig].filter(Boolean),
      terms: norm([label, unlisted?.keywords, routeKeywords, route.pathname].filter(Boolean).join(' ')),
      parentName: norm([unlisted?.parent, GROUP_LABELS[route.group]].filter(Boolean).join(' ')),
      parentKeywords: '',
    })
  })

  return entries
}

/**
 * Tokenised AND match: every word in the query must appear in the entry's own terms or in
 * its parent context, so word order doesn't matter ("dashboard ndr" finds the same page as
 * "ndr dashboard") and a parent name narrows its children ("ptl downloads").
 *
 * A single-word query must hit the entry's own terms or its parent's *name* — matching only
 * the parent's synonyms is not enough, or one word would drag in every sibling ("recharge"
 * would return all five Wallet pages instead of Wallet Recharges). Matching the parent name
 * does count, because "wallet" is a request for the Wallet section as a whole. Multi-word
 * queries are exempt entirely: "d2c downloads" deliberately names a section.
 *
 * Results are ranked label-match-first so the obvious hit lands on top.
 */
export function searchMenu(index, query) {
  const tokens = norm(query).trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return []

  const scored = []
  index.forEach((entry) => {
    const haystack = `${entry.terms} ${entry.parentName} ${entry.parentKeywords}`
    if (!tokens.every((t) => haystack.includes(t))) return
    if (tokens.length === 1) {
      const t = tokens[0]
      if (!entry.terms.includes(t) && !entry.parentName.includes(t)) return
    }

    const label = norm(entry.label)
    let score = 0
    if (label.startsWith(tokens[0])) score += 4
    if (tokens.every((t) => label.includes(t))) score += 3
    else if (tokens.some((t) => label.includes(t))) score += 1
    scored.push({entry, score})
  })

  return scored
    .sort((a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label))
    .map((s) => s.entry)
}
